/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { FC, useCallback, useMemo, useState } from 'react';

// redux
import { useSelector } from 'react-redux';
import { Store } from '../../../../../redux/root';

// icons
import { ReactComponent as ClockSvg } from 'assets/image/clock-challenges.svg';

// types
import { IChallengeResponse, SortDirection } from '@joc/api-gateway';

// styles
import styles from './ChallengesTable.module.scss';
import moment from 'moment-mini';
import AvatarCell from '../../../Table/CellRenderers/AvatarCell';
import cx from 'classnames';
import ChallengeActions from './ChallengeActions';
import ChallengeModal from '../ChallengeModal';
import Loader from '../../../Loader';
import ChallengePopupCongrats from '../ChallengePopupCongrats';
import ResponseFailure from '../../../ResponseFailure';
import ChallengesPreview from '../ChallengesPreview';
import TableHeadCellSortable from '../../../Table/TableHead/TableHeadCellSortable';
import { resolveNextSort } from 'core/functions/sort';
import {
	CHALLENGE_PHASE,
	CHALLENGE_TABLE_SORT,
	ChallengeTableSort,
	ChallengeTableSortBy,
	compareChallengesByPhase,
	getChallengePhase,
} from '../challengePhase';

const CHALLENGE_PHASE_LABELS = {
	[CHALLENGE_PHASE.OPEN]: 'Open',
	[CHALLENGE_PHASE.CLOSED]: 'Closed',
	[CHALLENGE_PHASE.SCHEDULED]: 'Scheduled',
} as const;

const CHALLENGE_PHASE_ROW_CLASS: Partial<Record<CHALLENGE_PHASE, string>> = {
	[CHALLENGE_PHASE.OPEN]: styles.challenges__row_open,
	[CHALLENGE_PHASE.CLOSED]: styles.challenges__row_closed,
};

export const ChallengesTable: FC<{ isSearching: boolean }> = ({ isSearching }) => {
	const [challengeToEdit, setChallengeToEdit] = useState<IChallengeResponse>();
	const [challengeToPreview, setChallengeToPreview] = useState<Partial<IChallengeResponse> | null>(null);
	const [isOpenChallengeCongratsModal, setIsOpenChallengeCongratsModal] = useState(false);
	const [sort, setSort] = useState<ChallengeTableSort>();

	const challengesRecords = useSelector((store: Store) => store.challengeRedux.challengesRecords);
	const isLoadingChallenges = useSelector((store: Store) => store.challengeRedux.isLoading);

	const challenges = useMemo(() => {
		if (!challengesRecords.length) {
			return [];
		}

		return challengesRecords.map(challenge => {
			const tempDiv = document.createElement('div');
			tempDiv.innerHTML = challenge.description
				.replace(/<\/p>/gi, ' ')
				.replace(/<p>/gi, '')
				.replace(/<br\s*\/?>/gi, ' ');

			return {
				...challenge,
				description: tempDiv.textContent || tempDiv.innerText || '',
			};
		});
	}, [challengesRecords]);

	const sortedChallenges = useMemo(() => {
		const list = [...challenges];

		if (sort?.sortBy === CHALLENGE_TABLE_SORT.PHASE) {
			return list.sort((a, b) => compareChallengesByPhase(a, b, sort.sortDir));
		}

		return list.sort((a, b) => a.id - b.id);
	}, [challenges, sort]);

	const handleSortChange = useCallback((sortBy: ChallengeTableSortBy, clickedDirection?: SortDirection) => {
		setSort(prevSort =>
			resolveNextSort({
				currentSort: prevSort,
				nextSortBy: sortBy,
				clickedDirection,
				allowClear: true,
			})
		);
	}, []);

	const editChallenge = (challenge: IChallengeResponse) => {
		setChallengeToEdit(challengesRecords.find(ch => ch.id === challenge.id));
	};

	const previewChallenge = (challenge: IChallengeResponse) => {
		setChallengeToPreview(challengesRecords.find(ch => ch.id === challenge.id) || null);
	};

	const handleCloseEditChallenge = (successSubmitType?: boolean) => {
		setChallengeToEdit(undefined);

		if (successSubmitType) {
			setIsOpenChallengeCongratsModal(true);
		}
	};

	return (
		<section className={styles.challenges__wrapper}>
			{challenges.length ? (
				<table className={styles.challenges}>
					<thead>
						<tr>
							<th align="left" className={styles.challenges_photo}>
								Photo
							</th>
							<th align="left" className={styles.challenges_title}>
								Challenge title
							</th>
							<th align="left" className={styles.challenges_subtitle}>
								Subtitle
							</th>
							<th align="left" className={styles.challenges_description}>
								Description
							</th>
							<th align="left" className={styles.challenges_startDate}>
								Date start
							</th>
							<th align="left" className={styles.challenges_deadline}>
								Deadline
							</th>

							<th align="left" className={styles.challenges_phase}>
								<TableHeadCellSortable
									title="Phase"
									sortBy={CHALLENGE_TABLE_SORT.PHASE}
									handleSortChange={handleSortChange}
									sort={sort}
								/>
							</th>
							<th align="left" aria-label="Actions" />
						</tr>
					</thead>
					<tbody>
						{sortedChallenges.map(challenge => {
							const phase = getChallengePhase(challenge);
							const phaseLabel = CHALLENGE_PHASE_LABELS[phase];

							return (
								<tr key={challenge.id} className={CHALLENGE_PHASE_ROW_CLASS[phase]}>
									<td className={styles.challenges_photo}>
										<AvatarCell imagePath={challenge.imagePath} />
									</td>
									<td
										className={cx(styles.challenges_title, styles.challenges_row_title)}
										title={challenge.name}
										style={{ cursor: 'pointer' }}
										onClick={() => editChallenge(challenge)}
									>
										<span>{challenge.name}</span>
									</td>
									<td
										className={cx(styles.challenges_subtitle, styles.challenges_row_subtitle)}
										title={challenge.title}
									>
										<span>{challenge.title}</span>
									</td>
									<td
										className={cx(styles.challenges_description, styles.challenges_row_description)}
										title={challenge.description}
									>
										<span>{challenge.description}</span>
									</td>
									<td className={styles.challenges_startDate}>
										<div className={styles.challenges_startDate_wrapper}>
											<ClockSvg />
											<span>
												Date start: {moment(challenge.startDate).format('MMMM DD, YYYY')}
											</span>
										</div>
									</td>
									<td className={cx(styles.challenges_deadline)}>
										<div className={styles.challenges_deadline_wrapper}>
											<ClockSvg />
											<span>Deadline: {moment(challenge.endDate).format('MMMM DD, YYYY')}</span>
										</div>
									</td>
									<td
										className={cx(styles.challenges_phase, styles.challenges_row_phase)}
										title={phaseLabel}
									>
										<span>{phaseLabel}</span>
									</td>
									<td align="center">
										<ChallengeActions
											challenge={challenge}
											onEditAction={editChallenge}
											onPreviewAction={previewChallenge}
										/>
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			) : isLoadingChallenges ? (
				<Loader loadProps={{ parentClassName: styles.loader }} />
			) : (
				<ResponseFailure
					styleTable={true}
					buttonUnshow={true}
					message={
						isSearching
							? "Sorry, we couldn't find any challenges matching your search. Please try again with different keywords or check back later."
							: "Sorry, we couldn't find any challenges at this time. Create them to see them here."
					}
				/>
			)}

			{challengeToEdit ? (
				<ChallengeModal
					isModalOpen={!!challengeToEdit}
					challenge={challengeToEdit}
					onClose={handleCloseEditChallenge}
				/>
			) : null}

			{challengeToPreview ? (
				<ChallengesPreview previewData={challengeToPreview} handlePreviewOpen={setChallengeToPreview} />
			) : null}

			{isOpenChallengeCongratsModal ? (
				<ChallengePopupCongrats setIsShowPopup={setIsOpenChallengeCongratsModal} isNew={false} />
			) : null}
		</section>
	);
};
